import { useMemo } from 'react';
import { CountdownCircleTimer } from 'react-countdown-circle-timer';
import { useFela } from 'react-fela';
import { FormattedMessage } from 'react-intl';

import { CopyButton, Text } from '@/modules/ui';
import type { Theme } from '@/styles/theme';

import { useTOTP } from '../../hooks/useTOTP';
import type { Secret } from '../../types';
import { getTOTPDuration } from '../../utilities/totp';
import * as felaRules from './TOTP.rules';

export interface TOTPProps {
    id: Secret['id'];
    isPubliclyShared?: boolean;
}

export const TOTP = ({ id, isPubliclyShared }: TOTPProps) => {
    const { theme, css } = useFela<Theme>();
    const { activeTotp, removeTOTP } = useTOTP(id, { autofetch: true, isPubliclyShared });

    const settings = useMemo(() => {
        if (activeTotp) {
            return {
                code: activeTotp.code,
                duration: getTOTPDuration(activeTotp),
            };
        }

        return {
            code: '',
            duration: 0,
        };
    }, [activeTotp]);

    return (
        <div className={css(felaRules.container)}>
            <CopyButton type='totp' key={id} text={settings.code} />
            <div className={css(felaRules.countdownContainer)}>
                <CountdownCircleTimer
                    key={settings.code}
                    isPlaying={Boolean(settings.duration)}
                    duration={30}
                    initialRemainingTime={settings.duration}
                    colors={settings.duration ? theme.colors.accentTertiary : theme.colors.foregroundsSeparator}
                    strokeLinecap='square'
                    strokeWidth={3}
                    size={24}
                    trailColor={theme.colors.foregroundsSeparator}
                    onComplete={() => {
                        removeTOTP(settings.code);
                    }}
                >
                    {({ remainingTime }) => <span className={css(felaRules.remainingTime)}>{remainingTime}</span>}
                </CountdownCircleTimer>
                <Text variant='ParagraphSmallMedium' color='accentTertiary'>
                    <FormattedMessage id='secret.totp.validity' />
                </Text>
            </div>
        </div>
    );
};
